import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Prayer } from '@/types/prayer';
import { usePrayerUpdate } from '@/hooks/usePrayerUpdate';
import { useSaveCue } from '@/hooks/useSaveCue';
import MicButton from './MicButton';
import styles from './UpdateComposer.module.css';

interface UpdateComposerProps {
  prayer: Prayer;
  voiceTarget: string;
  onCommit?: () => void;
  onClose: () => void;
}

/** Keeps the draft before an ordinary close; false holds an in-flight write. */
export interface UpdateComposerHandle {
  keepDraft: () => boolean;
}

/**
 * Shared composer body — textarea + mic + save-cue + actions row.
 * Wraps usePrayerUpdate + useSaveCue. The surrounding sheet shell
 * (slide-up, backdrop, header, prayer context) is the caller's
 * responsibility — this component owns ONLY the form.
 *
 * Shared by the prayer action sheet, Garden and person timeline.
 */
const UpdateComposer = forwardRef<UpdateComposerHandle, UpdateComposerProps>(function UpdateComposer(
  { prayer, voiceTarget, onCommit, onClose },
  ref,
) {
  const { t } = useTranslation();
  const update = usePrayerUpdate({ prayer, voiceTarget, onCommit });
  const showSaveCue = useSaveCue(
    update.isListening || update.isTranscribing,
    update.note,
  );

  const [confirmRevision, setConfirmRevision] = useState<number | null>(null);
  const keepWritingRef = useRef<HTMLButtonElement>(null);
  const discardRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const wasConfirming = useRef(false);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const handleClose = useCallback(() => {
    if (update.keepDraft()) onClose();
  }, [update, onClose]);
  useImperativeHandle(ref, () => ({ keepDraft: update.keepDraft }), [update.keepDraft]);
  // SheetShell owns Escape for the topmost sheet. Do not send a second close
  // from a window listener after its dialog has already handled the key.
  useEffect(() => {
    if (confirmRevision !== null) keepWritingRef.current?.focus({ preventScroll: true });
    else if (wasConfirming.current) {
      const target = discardRef.current?.disabled ? closeRef.current : discardRef.current;
      target?.focus({ preventScroll: true });
    }
    wasConfirming.current = confirmRevision !== null;
  }, [confirmRevision]);
  const cancelDiscard = () => setConfirmRevision(null);
  const confirmDiscard = async () => {
    if (confirmRevision === null) return;
    await update.discardDraft(confirmRevision);
    if (mounted.current) setConfirmRevision(null);
  };

  return (
    <div className={styles.body}>
      <textarea
        className={styles.textarea}
        value={update.displayValue}
        onChange={update.handleChange}
        placeholder={t('detail.updatePlaceholder')}
        aria-label={t('detail.updateInputAria')}
        rows={3}
        disabled={update.isSaving || !update.ready}
      />
      {update.loading !== 'ready' && (
        <div className={styles.draftNote} role="status">
          <p>{t(`updateDraft.${update.loading === 'multiple' ? 'multiple' : update.loading === 'failed' ? 'loadFailed' : 'loading'}`, { section: t('prayerRecovery.title') })}</p>
          {update.loading !== 'loading' && <button type="button" className={styles.retryRecovery} onClick={update.retryLoad}>{t('updateDraft.retry')}</button>}
        </div>
      )}
      {update.ready && update.note && <p className={styles.draftNote}>{t('updateDraft.lifetime')}</p>}
      {update.resolution !== 'none' && <div className={styles.draftNote} role="status">
        <p>{t(`updateDraft.${update.resolution}`)}</p>
        {update.resolution !== 'saved' && <button type="button" className={styles.retryRecovery}
          disabled={update.isSaving || !update.ready} onClick={update.retryLoad}>{t('updateDraft.retry')}</button>}
      </div>}
      {update.savedWithNewWords && <p className={styles.draftNote} role="status">{t('updateDraft.savedWithNewWords')}</p>}
      {update.discardError && <p className={styles.saveError} role="alert">{t('updateDraft.discardFailed')}</p>}
      {update.hasRecordingWords && <div className={styles.draftNote}>
        <p>{t('updateDraft.recordingKept')}</p>
        <button type="button" className={styles.retryRecovery} onClick={update.useRecordingWords} disabled={update.isSaving || !update.ready}>{t('updateDraft.useRecordingWords')}</button>
      </div>}
      {(update.saveError || update.hasFailedRecovery || update.hasParkedRecovery) && (
        <div className={styles.saveError} role="alert">
          <span>
            {t(
              update.hasParkedRecovery
                ? 'detail.recoveryParked'
                : update.hasFailedRecovery
                  ? 'detail.recoveryFailed'
                : update.saveNeedsReopen
                  ? 'voice.saveNeedsReopen'
                : 'detail.saveError',
            )}
          </span>
          {update.canRetryRecovery && (
            <button
              type="button"
              className={styles.retryRecovery}
              onClick={update.retryRecovery}
              disabled={update.isSaving || !update.ready}
            >
              {t('detail.retryRecovery')}
            </button>
          )}
        </div>
      )}
      {update.isSupported && (
        <div className={styles.micArea}>
          <MicButton
            size="lg"
            isListening={update.isListening}
            isTranscribing={update.isTranscribing}
            showLabel
            onClick={update.toggleMic}
            disabled={update.isSaving || !update.ready}
            ariaLabel={update.isListening ? t('detail.stopRecording') : t('detail.voiceInput')}
          />
        </div>
      )}
      {/* The microphone remains the sole action during capture/processing. */}
      {!update.isListening && !update.isTranscribing && (
        <>
          {showSaveCue && (
            <div className={styles.hint} aria-hidden="true">
              {t('detail.updateHint')}
            </div>
          )}
          <div className={`${styles.actions} ${styles.draftSaveActions}`} data-keyboard-actions="">
            <div className={styles.spacer} />
            <button ref={closeRef} type="button" className={styles.cancel} onClick={handleClose} disabled={update.isSaving}>
              {t('updateDraft.close')}
            </button>
            <button
              type="button"
              className={`${styles.submit} ${showSaveCue ? styles.submitCue : ''}`}
              onClick={() => void (update.resolution === 'saved' ? update.commitSuccessor() : update.commitUpdate())}
              disabled={(!update.canSubmit && !update.canSaveSuccessor) || update.isSaving}
            >
              {t(update.resolution === 'saved' ? 'updateDraft.saveAnother' : 'detail.addUpdateBtn')}
            </button>
          </div>
          {confirmRevision === null ? (
            <button ref={discardRef} type="button" className={styles.discardDraft}
              disabled={!update.ready || update.isSaving || update.resolution === 'changed' || !update.note.trim()}
              onClick={() => setConfirmRevision(update.revision)}>{t('updateDraft.discard')}</button>
          ) : (
            <div className={styles.discardConfirmation} role="group" aria-label={t('updateDraft.confirm')}>
              <p>{t('updateDraft.confirm')}</p>
              {update.hasRetainedRecording && <p>{t('updateDraft.recordingKept')}</p>}
              <div className={styles.draftActions}>
                <button ref={keepWritingRef} type="button" className={styles.cancel} disabled={update.isSaving} onClick={cancelDiscard}>{t('updateDraft.keepWriting')}</button>
                <button type="button" className={styles.confirmDiscard} disabled={update.isSaving} onClick={() => void confirmDiscard()}>{t('updateDraft.discard')}</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
});

export default UpdateComposer;
